import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  RowCustomAction
} from '@globaleris/ui.library'
import { Visibility } from '@material-ui/icons'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { EnumAtlasEntityType } from '../../../../../api/Argentia/endpoints/atlasAccess/model/atlasAccess'
import { StandardCard } from '../../../../../components/shared/standardCard/standardCard'
import { getCustomerDomain } from '../../../../../redux'

export interface ClientAccessModel {
  entityId: number
  entityName: string
  entityType: EnumAtlasEntityType
  usersCount: number
}

interface ClientsAccessTableProps {
  data: ClientAccessModel[]
  isLoading?: boolean
  onRowInsert?: () => void
  onViewDetail?: (row: ClientAccessModel) => void
}

export const ClientsAccessTable: React.FC<ClientsAccessTableProps> = ({
  data,
  isLoading,
  onRowInsert,
  onViewDetail
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'atlasAccess'])

  const noData = !isLoading && data && data.length === 0

  // el DataTable no soporta un renderer custom por celda acá - se precomputa el label traducido
  const tableData = data.map(row => ({
    ...row,
    entityTypeLabel: t(
      `atlasAccess:components.clientsAccessTable.entityTypes.${row.entityType === EnumAtlasEntityType.Client ? 'client' : 'commercial'}`
    )
  }))

  // #region Functions Starts
  const getRowCustomActions = (): RowCustomAction[] => {
    const actions: RowCustomAction[] = []

    if (onViewDetail) {
      actions.push({
        icon: <Visibility fontSize="small" />,
        tooltip: t('atlasAccess:components.clientsAccessTable.tooltips.viewDetail'),
        onClick: onViewDetail
      })
    }

    return actions
  }
  // #endregion Functions Ends

  // #region DataTable Config Starts
  const clientsAccessTableColumns: DataTableColumn[] = [
    {
      name: 'entityId',
      display: false
    },
    {
      name: 'entityName',
      label: t('atlasAccess:components.clientsAccessTable.columns.entityName'),
      type: DataType.TEXT
    },
    {
      name: 'entityTypeLabel',
      label: t('atlasAccess:components.clientsAccessTable.columns.entityType'),
      type: DataType.TEXT
    },
    {
      name: 'usersCount',
      label: t('atlasAccess:components.clientsAccessTable.columns.usersCount'),
      type: DataType.NUMBER
    }
  ]

  const clientsAccessTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    filterType: FilterType.ADVANCED,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: !!onRowInsert,
    customInsertEditModal: true,
    onRowInsert: onRowInsert
      ? async () => {
          onRowInsert()
          return true
        }
      : undefined,
    pagination: true,
    analytics: {
      customer: getCustomerDomain(),
      view: t('atlasAccess:components.clientsAccessTable.analytics')
    },
    rowCustomActions: getRowCustomActions()
  }
  // #endregion DataTable Config Ends

  return (
    <StandardCard
      variant="outlined"
      title={t('atlasAccess:components.clientsAccessTable.title')}
      showEmptyContent={noData}
      emptyCaption={t('atlasAccess:components.clientsAccessTable.emptyCaption')}
      emptyButtonCallback={onRowInsert}
      emptyButtonCaption={
        onRowInsert ? t('atlasAccess:components.clientsAccessTable.insertTitle') : undefined
      }
    >
      <DataTable
        data={isLoading ? undefined : tableData}
        columns={clientsAccessTableColumns}
        options={clientsAccessTableOptions}
      />
    </StandardCard>
  )
}
